import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL; 
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('storepointe_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Silently ignore 429 on all routes except login
    if (error.response?.status === 429) {
      const isLogin = error.config?.url?.includes('/auth/login');
      if (!isLogin) return Promise.resolve(null);
    }
    let message = error.response?.data?.message || error.message || 'Something went wrong';
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      message = error.response.data.errors.map(err => err.message).join(', ');
    }
    if (error.response?.status === 401) {
      const isLogin = error.config?.url?.includes('/auth/login');
      if (!isLogin) {
        localStorage.removeItem('storepointe_token');
        localStorage.removeItem('storepointe_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(new Error(message));
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email, clientUrl) => api.post('/auth/forgot-password', { email, clientUrl }),
  resetPassword: (token, data) => api.patch(`/auth/reset-password/${token}`, data),
};

// Supermarkets
export const supermarketAPI = {
  getAll: (params) => api.get('/supermarkets', { params }),
  getById: (id) => api.get(`/supermarkets/${id}`),
  create: (data) => api.post('/supermarkets', data),
  update: (id, data) => api.patch(`/supermarkets/${id}`, data),
  approve: (id) => api.patch(`/supermarkets/${id}/approve`),
  reject: (id, reason) => api.patch(`/supermarkets/${id}/reject`, { reason }),
  toggleStatus: (id) => api.patch(`/supermarkets/${id}/toggle-status`),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.patch(`/supermarkets/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getStats: (id) => api.get(`/supermarkets/${id}/stats`),
};

// Users
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  suspend: (id, reason) => api.patch(`/users/${id}/suspend`, { reason }),
  activate: (id) => api.patch(`/users/${id}/activate`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
  registerAdmin: (data) => api.post('/auth/register-admin', data),
};

// Orders
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
};

// Audit Logs
export const auditAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
};

// Analytics
export const analyticsAPI = {
  getSystem: (params) => api.get('/analytics/system', { params }),
  getRevenue: (params) => api.get('/analytics/system/revenue', { params }),
  getSupermarket: (id, params) => api.get(`/analytics/supermarket/${id}`, { params }),
};

// Notifications
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Delivery & Live Tracking
export const deliveryAPI = {
  getSettings: () => api.get('/delivery/settings'),
  updateSettings: (data) => api.patch('/delivery/settings', data),
  getOrders: (params) => api.get('/delivery/orders', { params }),
  trackOrder: (id) => api.get(`/delivery/orders/${id}/track`),
  dispatchOrder: (id, data) => api.post(`/delivery/orders/${id}/dispatch`, data),
  getShareLink: (id) => api.post(`/delivery/orders/${id}/share-link`),
};

